import { useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  FormGroup,
  FormLabel,
  Grid,
  LinearProgress,
  OutlinedInput,
  Typography,
} from "@material-ui/core";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import Api from "lib/api/httpRequestHelper";
import UtilsFncs from "utils/UtilsFncs";

import TableHistoricoImportacao from "./representante-x-cidade/TableHistoricoImportacao";
import {
  getImportSnackbarMessage,
  getImportSnackbarSeverity,
  getImportSummary,
  STATUS_PARCIAL,
} from "./representante-x-cidade/importStatus";
import {
  readRepresentanteCidadeWorkbook,
  validateRepresentanteCidadeFile,
  validateRepresentanteCidadeXlsx,
} from "./representante-x-cidade/validateRepresentanteCidadeXlsx";

const MAX_FILE_SIZE = 20 * 1024 * 1024;

function formatFileSize(bytes) {
  if (!bytes) return "";
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / 1024 ** i).toFixed(2)} ${sizes[i]}`;
}

export default function RepresentanteXCidadePage() {
  const snackbar = useSnackbar();
  const xlsxInputRef = useRef(null);

  const [fileState, setFileState] = useState({
    base64: null,
    name: "",
    size: 0,
  });
  const [fileError, setFileError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [historyList, setHistoryList] = useState([]);

  async function loadHistory(showLoading = false) {
    if (showLoading) setSearching(true);

    try {
      const response = await Api.get(
        "list-representante-cidade-import-log",
        "adminportal",
        "",
        true,
      );

      const formattedList =
        response.Result?.map((item) => ({
          idRepresentanteCidadeImportacaoLog:
            item.IdRepresentanteCidadeImportacaoLog,
          idUsuario: item.IdUsuario,
          nome: item.Nome,
          dataAlteracao: item.DataAlteracao,
          statusProcesso: item.StatusProcesso,
          mensagem: item.Mensagem,
          resumo: getImportSummary(item.Mensagem),
        })) ?? [];

      setHistoryList(formattedList);
    } catch (error) {
      snackbar.showMessage(
        error.message || "Erro ao buscar histórico de importações",
        { severity: "error" },
      );
    } finally {
      setSearching(false);
    }
  }

  async function handleFileChange(event) {
    const file = event.target.files?.[0];

    if (!file) {
      setFileState({ base64: null, name: "", size: 0 });
      setFileError(null);
      return;
    }

    const extensionError = validateRepresentanteCidadeFile(file);
    if (extensionError) {
      setFileState({ base64: null, name: "", size: 0 });
      setFileError(extensionError);
      if (xlsxInputRef.current) xlsxInputRef.current.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFileState({ base64: null, name: "", size: 0 });
      setFileError("Arquivo muito grande (máximo 20MB)");
      if (xlsxInputRef.current) xlsxInputRef.current.value = "";
      return;
    }

    try {
      const workbook = await readRepresentanteCidadeWorkbook(file);
      const layoutError = validateRepresentanteCidadeXlsx(workbook);

      if (layoutError) {
        setFileState({ base64: null, name: "", size: 0 });
        setFileError(layoutError);
        if (xlsxInputRef.current) xlsxInputRef.current.value = "";
        return;
      }

      const base64Data = await UtilsFncs.FileToB64(file);
      setFileState({
        base64: base64Data,
        name: file.name,
        size: file.size,
      });
      setFileError(null);
    } catch (error) {
      setFileState({ base64: null, name: "", size: 0 });
      setFileError(
        error.message ||
          "Não foi possível validar o arquivo. Verifique o layout do modelo.",
      );
      if (xlsxInputRef.current) xlsxInputRef.current.value = "";
    }
  }

  function resetFileInput() {
    if (xlsxInputRef.current) xlsxInputRef.current.value = "";
    setFileState({ base64: null, name: "", size: 0 });
    setFileError(null);
  }

  async function handleUpload() {
    if (!fileState.base64) {
      setFileError("Arquivo XLSX é obrigatório");
      return;
    }

    setIsUploading(true);

    try {
      const encodedXlsxBase64 = fileState.base64
        ?.toString()
        ?.replace(/^data:(.*,)?/, "");

      const response = await Api.post(
        "import-representante-cidade",
        "adminportal",
        { arquivoXlsxBase64: encodedXlsxBase64 },
        true,
      );

      const log = response?.Result
        ? {
            statusProcesso: response.Result.StatusProcesso,
            mensagem: response.Result.Mensagem,
          }
        : null;

      snackbar.showMessage(getImportSnackbarMessage(log), {
        severity: getImportSnackbarSeverity(log?.statusProcesso),
        ...(log?.statusProcesso === STATUS_PARCIAL && log?.mensagem
          ? { autoHideDuration: 12000 }
          : {}),
      });

      resetFileInput();
      await loadHistory();
    } catch (error) {
      snackbar.showMessage(error.message || "Erro ao enviar arquivo", {
        severity: "error",
      });
      await loadHistory();
    } finally {
      setIsUploading(false);
    }
  }

  useEffect(() => {
    loadHistory(true);
  }, []);

  const isSubmitDisabled = !fileState.base64 || isUploading || !!fileError;

  return (
    <>
      <HeaderClientes
        section="Representante x Cidade"
        subsection="Administração"
      />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <Grid container>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography
                  variant="h2"
                  style={{
                    color: "#002147",
                    fontWeight: 700,
                  }}
                >
                  Representante x Cidade
                </Typography>

                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                  Envie o arquivo modelo (.xlsx) para atualizar o vínculo entre
                  representantes e cidades.
                </Typography>

                <Divider style={{ margin: "1.5rem 0 2rem" }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6} lg={5}>
                    <FormGroup>
                      <FormLabel style={{ textTransform: "none", marginBottom: 8 }}>
                        Arquivo de importação
                      </FormLabel>
                      <OutlinedInput
                        id="uploadRepresentanteXlsx"
                        fullWidth
                        type="file"
                        inputRef={xlsxInputRef}
                        onChange={handleFileChange}
                        disabled={isUploading}
                        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        error={!!fileError}
                      />
                      {fileError && (
                        <Typography color="error" variant="caption">
                          {fileError}
                        </Typography>
                      )}
                      {fileState.name && !fileError && (
                        <Typography variant="caption" style={{ marginTop: 8 }}>
                          {fileState.name} ({formatFileSize(fileState.size)})
                        </Typography>
                      )}
                    </FormGroup>

                    <Box mt={3}>
                      <Button
                        disabled={isSubmitDisabled}
                        variant="contained"
                        color="primary"
                        onClick={handleUpload}
                        endIcon={
                          isUploading ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <CloudUploadIcon />
                          )
                        }
                      >
                        {isUploading ? "Enviando" : "Enviar"} catálogo
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {searching ? (
              <Box mt={3}>
                <Typography variant="body2">
                  Buscando histórico de alterações...
                </Typography>
                <LinearProgress />
              </Box>
            ) : (
              <TableHistoricoImportacao
                data={historyList}
                searching={searching}
              />
            )}
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
